import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router';
import './PrivacyPolicy.scss';

const privacySchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Privacy Policy',
  url: 'https://www.presstoindia.com/privacy-policy',
  description: 'How Pressto India collects, uses, and protects personal information, including location data, on the customer web platform and Rider App.',
  publisher: {
    '@type': 'Organization',
    name: 'Pressto India',
    url: 'https://www.presstoindia.com/',
  },
};

const PrivacyPolicy = () => {
  return (
    <div className="privacy-page">
      <Helmet>
        <title>Privacy Policy | Pressto India</title>
        <meta
          name="description"
          content="Read how Pressto India collects, uses, and protects personal information, including location data, through the customer web platform and Rider App."
        />
        <link rel="canonical" href="https://www.presstoindia.com/privacy-policy" />
        <script type="application/ld+json">{JSON.stringify(privacySchema)}</script>
      </Helmet>

      <article className="privacy-content">
        <h1>Privacy Policy</h1>
        <p className="privacy-updated">Last updated: 10 October 2026</p>
        <p>
          Pressto India (“Pressto”, “we”, “us”) provides premium laundry, dry cleaning, and shoe and bag care. This policy explains what personal information we collect through our customer web platform, Rider App, stores, pickup and drop services, and related customer support, and how we use it.
        </p>

        <h2>Customer Web Platform, Rider App and Location Data</h2>
        <p>
          As part of our upgraded platform, Pressto provides a customer-facing web platform that enables customers to place and track orders, and a Rider App used by our delivery executives to record pickups, drop-offs and handovers. Through these services (collectively, the “Services”), we collect and process the following information:
        </p>

        <h3>1. Customer Information and Usage Data</h3>
        <p>
          We collect information you provide while using our customer web platform, including your name, phone number, email address, delivery address, order details and preferences. We may also collect information generated through your use of the Services, including order and delivery history, device and browser type, IP address and usage logs.
        </p>
        <p>
          We use this information to process orders, coordinate deliveries, communicate with customers, improve our Services and provide a better customer experience.
        </p>

        <h3>2. Location Data and Permissions</h3>
        <p>
          With your permission, we may collect precise or approximate location data through GPS, Wi-Fi or mobile network signals, subject to the capabilities of your device, browser and the relevant application.
        </p>
        <p>
          For customers, location data may be collected while using our customer web platform, where location access is supported and permission has been granted. For riders and delivery executives, location data may be collected through the Rider App while it is in use and, where supported by the device and operating system and where the necessary permissions have been granted, in the background.
        </p>
        <p>We use location data for the following purposes:</p>
        <ul>
          <li>To coordinate and verify customer pickups and deliveries.</li>
          <li>To provide relevant service updates, offers and notifications through push notifications, in-platform messages, email and WhatsApp, where applicable.</li>
          <li>To trigger geofenced reminders when a customer or rider enters or exits predefined areas near our stores, where the relevant technology and permissions support this functionality. These reminders are based on discrete entry or exit events and are not intended to involve continuous location tracking for this purpose.</li>
          <li>To improve and personalise our Services and customer experience.</li>
        </ul>
        <p>
          You may withdraw location permission at any time through your device or browser settings. Certain location-dependent features may not function as intended if permission is denied or withdrawn.
        </p>

        <h3>3. Rider and Delivery Partner Information</h3>
        <p>
          For riders and delivery partners using the Rider App, we may collect identity, onboarding and operational information, including name, phone number, photograph, address, vehicle type, driving licence details, bank account details required for payouts and emergency contact details.
        </p>
        <p>
          Through the Rider App, we may record pickup, drop-off and handover activities, including timestamps, associated order details and location data. We use this information to onboard and verify riders, assign and track orders, process payouts, coordinate deliveries, maintain operational records, support safety and comply with applicable legal obligations.
        </p>

        <h3>4. Location Data Sharing and Retention</h3>
        <p>
          Location data collected for geofencing is not sold or shared with third parties for their independent use. It may be processed by Pressto and its authorised analytics and customer relationship management (CRM) service providers for the purposes described in this Privacy Policy, subject to applicable confidentiality, contractual and data protection obligations.
        </p>
        <p>
          We retain location data only for as long as necessary to fulfil the purposes for which it was collected, meet operational requirements and comply with applicable legal obligations. Data that is no longer required will be deleted or otherwise handled in accordance with applicable law and our data retention practices.
        </p>

        <h3>5. Privacy Queries</h3>
        <p>
          For questions or concerns regarding the collection, use, storage or processing of personal information, including location data, please contact us at <a href="mailto:help@presstoindia.com">help@presstoindia.com</a>.
        </p>

        <h2>How we share information</h2>
        <p>We do not sell your personal information. We share it only when it is needed to run the service:</p>
        <ul>
          <li>With Pressto stores, riders and staff who handle your order.</li>
          <li>With authorised analytics and customer relationship management (CRM) service providers, as described above.</li>
          <li>With service providers that send email on our behalf, host our systems, or measure website use. These include EmailJS for form messages, Google Tag Manager, and Microsoft Clarity.</li>
          <li>With WhatsApp when you choose to message us there, or where we send applicable service updates.</li>
          <li>When the law requires us to disclose information, or when it is needed to protect our customers, staff, or business.</li>
        </ul>

        <h2>Cookies and similar technologies</h2>
        <p>
          The website uses cookies and similar technologies to remember basic site behaviour and to measure traffic. Google Tag Manager and Microsoft Clarity help us see which pages are used and where visitors have trouble. You can control cookies in your browser settings. Blocking cookies may limit some site features.
        </p>

        <h2>How long we keep information</h2>
        <p>
          We keep personal information for as long as your customer or rider relationship continues and for the period needed to complete orders, process payouts, handle claims, and meet tax and legal record-keeping duties. Location data is retained only for as long as necessary for the purposes described above. When information is no longer needed, we delete it or remove details that identify you.
        </p>

        <h2>How we protect information</h2>
        <p>
          We use access controls and service-provider agreements to limit who can see customer information. No method of transmission or storage is completely secure, so we cannot guarantee absolute security.
        </p>

        <h2>Your choices and rights</h2>
        <p>You may ask us to:</p>
        <ul>
          <li>Confirm what personal information we hold about you.</li>
          <li>Correct information that is inaccurate.</li>
          <li>Delete information we no longer need to provide a service or keep by law.</li>
          <li>Stop sending promotional updates. Service messages about an active order may still be sent.</li>
          <li>Withdraw location permission at any time through your device or browser settings.</li>
        </ul>
        <p>
          To make a request, email <a href="mailto:help@presstoindia.com">help@presstoindia.com</a> or <a href="mailto:info@presstoindia.com">info@presstoindia.com</a>, or call <a href="tel:1800229199">1800229199</a>. We may need to confirm your identity before we act on the request.
        </p>

        <h2>Children</h2>
        <p>
          Our services are meant for adults. We do not knowingly collect personal information from children. If you believe a child has given us personal information, contact us and we will delete it.
        </p>

        <h2>Changes to this policy</h2>
        <p>
          We may update this policy when our services or legal duties change. The date at the top of this page shows when it was last revised. Continued use of the website after an update means you have seen the revised policy.
        </p>

        <h2>Contact us</h2>
        <p>
          Pressto India<br />
          Head office, Lower Parel, Mumbai 400013<br />
          Privacy queries: <a href="mailto:help@presstoindia.com">help@presstoindia.com</a><br />
          Email: <a href="mailto:info@presstoindia.com">info@presstoindia.com</a><br />
          Phone: <a href="tel:1800229199">1800229199</a><br />
          Website: <Link to="/">www.presstoindia.com</Link>
        </p>
      </article>
    </div>
  );
};

export default PrivacyPolicy;
